import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'

const API_URL = process.env.REACT_APP_API_URL

function parseApiResponse(raw) {
  let d = raw
  if (typeof d === 'string') {
    try {
      d = JSON.parse(d)
    } catch {
      return raw
    }
  }
  if (d?.body != null) {
    if (typeof d.body === 'string') {
      try {
        return JSON.parse(d.body)
      } catch {
        return d.body
      }
    }
    return d.body
  }
  return d
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== 'object') return false
  const s = responseData.success
  return s === true || s === 'true' || s === 1 || s === '1'
}

function parseAdditionalCcEmails(value) {
  return String(value || '')
    .split(/[,;]+/)
    .map((email) => email.trim())
    .filter(Boolean)
}

function isValidEmailAddress(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim())
}

function buildCcEmails(userEmail, additionalInput) {
  const result = []
  const seen = new Set()
  const addEmail = (email) => {
    const trimmed = String(email || '').trim()
    if (!trimmed) return
    const key = trimmed.toLowerCase()
    if (seen.has(key)) return
    seen.add(key)
    result.push(trimmed)
  }
  addEmail(userEmail)
  parseAdditionalCcEmails(additionalInput).forEach(addEmail)
  return result
}

function getMaterialGstPercent(row) {
  if (row?.gstPercent != null && row.gstPercent !== '') return row.gstPercent
  if (row?.gst != null && row.gst !== '') return row.gst
  return null
}

function formatMaterialMoney(value) {
  if (value == null || value === '') return '-'
  const n = parseFloat(value)
  return Number.isFinite(n) ? n.toFixed(2) : String(value)
}

function getMaterialGstValueDisplay(row) {
  if (row?.gstValue != null && row.gstValue !== '') {
    return formatMaterialMoney(row.gstValue)
  }
  const basic = parseFloat(row?.basicPrice) || 0
  const qty = parseFloat(row?.quantity) || 0
  const gst = parseFloat(getMaterialGstPercent(row)) || 0
  if (basic > 0 && qty > 0 && gst > 0) {
    return ((basic * qty) * (gst / 100)).toFixed(2)
  }
  return '-'
}

function getMaterialTotalValueDisplay(row) {
  if (row?.totalValue != null && row.totalValue !== '') {
    return formatMaterialMoney(row.totalValue)
  }
  const basic = parseFloat(row?.basicPrice) || 0
  const qty = parseFloat(row?.quantity) || 0
  const gst = parseFloat(getMaterialGstPercent(row)) || 0
  if (basic > 0 && qty > 0) {
    const totalBasic = basic * qty
    const gstAmount = totalBasic * (gst / 100)
    return (totalBasic + gstAmount).toFixed(2)
  }
  return '-'
}

function calcGrossTotalsFromMaterials(materialRows) {
  return materialRows.reduce(
    (acc, row) => {
      const lineBasic = (parseFloat(row?.basicPrice) || 0) * (parseFloat(row?.quantity) || 0)
      let gstValue = parseFloat(row?.gstValue)
      if (!Number.isFinite(gstValue)) {
        const gst = parseFloat(getMaterialGstPercent(row)) || 0
        gstValue = (lineBasic * gst) / 100
      }
      let totalValue = parseFloat(row?.totalValue)
      if (!Number.isFinite(totalValue)) {
        totalValue = lineBasic + gstValue
      }
      return {
        grossTotalBasicPrice: acc.grossTotalBasicPrice + lineBasic,
        grossGstValue: acc.grossGstValue + gstValue,
        grossTotalValue: acc.grossTotalValue + totalValue
      }
    },
    { grossTotalBasicPrice: 0, grossGstValue: 0, grossTotalValue: 0 }
  )
}

function formatDate(value) {
  if (!value) return '-'
  const str = String(value).trim()
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str.replace(/-/g, '/')
  const dateTimeMatch = str.match(/^(\d{2})-(\d{2})-(\d{4})[ T]/)
  if (dateTimeMatch) {
    return `${dateTimeMatch[1]}/${dateTimeMatch[2]}/${dateTimeMatch[3]}`
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return str || '-'
  return formatDateParts(d)
}

function formatDateParts(date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date)
  const get = (type) => parts.find((p) => p.type === type)?.value || ''
  return `${get('day')}/${get('month')}/${get('year')}`
}

function formatDateTime(value) {
  if (!value) return '-'
  const str = String(value).trim()
  const dateTimeMatch = str.match(/^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2})/)
  if (dateTimeMatch) {
    const [, dd, mm, yyyy, hh, min] = dateTimeMatch
    const hour = parseInt(hh, 10)
    const period = hour >= 12 ? 'PM' : 'AM'
    const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    return `${dd}/${mm}/${yyyy}, ${String(h12).padStart(2, '0')}:${min} ${period}`
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return str || '-'
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).formatToParts(d)
  const get = (type) => parts.find((p) => p.type === type)?.value || ''
  const dayPeriod = get('dayPeriod').toUpperCase()
  return `${get('day')}/${get('month')}/${get('year')}, ${get('hour')}:${get('minute')} ${dayPeriod}`
}

function displayUser(ref) {
  if (!ref) return '-'
  if (typeof ref === 'string') return ref
  if (ref.name) return ref.name
  if (ref.email) return ref.email
  return '-'
}

function capitalizeStatus(value) {
  if (!value) return '-'
  const s = String(value)
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}

function hasPoPdf(po) {
  return Boolean(String(po?.pdfDownloadUrl || po?.pdfLink || '').trim())
}

function getApproverEmail(po) {
  return String(po?.approverDetails?.email || po?.approverEmail || '').trim()
}

function getApproverName(po) {
  return String(po?.approverDetails?.name || po?.approverName || '').trim()
}

function getSupplierName(po) {
  return String(
    po?.supplierName || po?.supplierDetails?.name || ''
  ).trim()
}

function getSupplierContacts(po) {
  if (Array.isArray(po?.supplierContacts) && po.supplierContacts.length > 0) {
    return po.supplierContacts
      .map((c) => ({
        supplierName: String(
          c.supplierName || po?.supplierName || po?.supplierDetails?.name || ''
        ).trim(),
        supplierMailId: String(c.supplierMailId || c.email || '').trim(),
        supplierPhoneNumber: String(
          c.supplierPhoneNumber || c.phonenumber || c.phoneNumber || ''
        ).trim()
      }))
      .filter((c) => c.supplierMailId || c.supplierName || c.supplierPhoneNumber)
  }

  // Legacy fallback when supplierContacts is not present
  const legacyMail = String(
    po?.supplierMailId ||
      po?.supplierEmail ||
      po?.email ||
      po?.supplierDetails?.email ||
      ''
  ).trim()
  const legacyMobile = String(
    po?.supplierMobile ||
      po?.supplierDetails?.phoneNumber ||
      po?.supplierDetails?.phonenumber ||
      po?.supplierDetails?.mobile ||
      po?.phonenumber ||
      po?.phoneNumber ||
      ''
  ).trim()
  const legacyName = getSupplierName(po)
  if (!legacyMail && !legacyMobile && !legacyName) return []
  return [
    {
      supplierName: legacyName,
      supplierMailId: legacyMail,
      supplierPhoneNumber: legacyMobile
    }
  ]
}

const ViewField = ({ label, children }) => (
  <div className="form-group row align-items-center">
    <label className="col-12 col-md-4 col-form-label">{label}</label>
    <div className="col-12 col-md-8">
      <div className="txtdsply">{children}</div>
    </div>
  </div>
)

const SectionTitle = ({ icon, title }) => (
  <div className="row mb-2 mt-3">
    <div className="col-12">
      <h5 className="text-primary border-bottom pb-2 mb-3">
        <i className={`fa-solid ${icon} mr-2`} />
        {title}
      </h5>
    </div>
  </div>
)

function PoView() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [po, setPo] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isResending, setIsResending] = useState(false)
  const [resendingSupplierFor, setResendingSupplierFor] = useState('')
  const [isOpeningPdf, setIsOpeningPdf] = useState(false)
  const [downloadingS3Key, setDownloadingS3Key] = useState('')
  const [userEmail, setUserEmail] = useState('')
  const [additionalCcEmails, setAdditionalCcEmails] = useState('')

  const fetchPo = useCallback(async () => {
    if (!id) {
      toast.error('PO ID is missing', { autoClose: 2000 })
      navigate('/po')
      return
    }

    setIsLoading(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-po-detail-by-id',
        poId: id
      })
      const responseData = parseApiResponse(response.data)

      if (isApiSuccess(responseData) && responseData?.data) {
        setPo(responseData.data)
      } else {
        toast.error(responseData?.message || 'Failed to fetch PO details', { autoClose: 2000 })
        navigate('/po')
      }
    } catch (error) {
      console.error('Fetch PO error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(errData?.message || 'An error occurred while fetching PO details', { autoClose: 2000 })
      navigate('/po')
    } finally {
      setIsLoading(false)
    }
  }, [id, navigate])

  useEffect(() => {
    fetchPo()
  }, [fetchPo])

  useEffect(() => {
    const fetchUserEmail = async () => {
      const userId = localStorage.getItem('id')
      if (!userId) return
      try {
        const response = await axios.post(API_URL, { action: 'get-user', id: userId })
        const responseData = parseApiResponse(response.data)
        if (isApiSuccess(responseData) && responseData.user?.email) {
          setUserEmail(String(responseData.user.email).trim())
        }
      } catch (error) {
        console.error('Fetch user email error:', error)
      }
    }
    fetchUserEmail()
  }, [])

  const materials = useMemo(
    () => (Array.isArray(po?.materials) ? po.materials : []),
    [po]
  )
  const terms = useMemo(() => (Array.isArray(po?.terms) ? po.terms : []), [po])
  const milestones = useMemo(
    () => (Array.isArray(po?.milestone) ? po.milestone : []),
    [po]
  )
  const owners = useMemo(() => (Array.isArray(po?.poOwner) ? po.poOwner : []), [po])
  const supplierContacts = useMemo(() => getSupplierContacts(po), [po])
  const materialGrossTotals = useMemo(
    () => calcGrossTotalsFromMaterials(materials),
    [materials]
  )

  const poStatusLower = String(po?.poStatus || '').toLowerCase()
  const isApproved = poStatusLower === 'approved'
  const isWaitingForApproval = poStatusLower === 'waiting for approval'
  const canResendApproverEmail = isWaitingForApproval || isApproved
  const canResendSupplierEmail = isApproved
  const showResendEmailSection = canResendApproverEmail || canResendSupplierEmail
  const approverEmail = getApproverEmail(po)
  const approverName = getApproverName(po)

  const handleResendEmail = async () => {
    const userId = localStorage.getItem('id')
    if (!id) {
      toast.error('PO ID is missing', { autoClose: 2000 })
      return
    }
    if (!userId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      return
    }
    if (!canResendApproverEmail) {
      toast.error(
        'Approver email can be resent only when PO is Waiting for approval or Approved',
        { autoClose: 2000 }
      )
      return
    }
    if (!approverEmail) {
      toast.error('Approver email is missing', { autoClose: 2000 })
      return
    }

    const ccEmails = buildCcEmails(userEmail, additionalCcEmails)
    const invalidCc = ccEmails.find((email) => !isValidEmailAddress(email))
    if (invalidCc) {
      toast.error(`Invalid CC email: ${invalidCc}`, { autoClose: 2000 })
      return
    }

    setIsResending(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'resend-po-email',
        poId: id,
        userid: userId,
        toEmails: [approverEmail],
        ccEmails,
        subject: 'PO Reminder',
        content: `Reminder to approve PO ${po?.poNumber || ''}.`
      })
      const responseData = parseApiResponse(response.data)

      if (isApiSuccess(responseData)) {
        const failCount = Number(responseData?.data?.failCount) || 0
        if (failCount > 0) {
          toast.error(responseData?.message || 'Failed to resend email', { autoClose: 2000 })
          return
        }
        toast.success(responseData?.message || `Email resent to ${approverEmail}`, {
          autoClose: 2000
        })
        setPo((prev) => (prev ? { ...prev, isMailSent: true } : prev))
      } else {
        toast.error(responseData?.message || 'Failed to resend email', { autoClose: 2000 })
      }
    } catch (error) {
      console.error('Resend PO email error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(errData?.message || 'An error occurred while resending email', { autoClose: 2000 })
    } finally {
      setIsResending(false)
    }
  }

  const handleResendSupplierEmail = async (contact) => {
    const supplierMailId = String(contact?.supplierMailId || '').trim()
    const userId = localStorage.getItem('id')

    if (!id) {
      toast.error('PO ID is missing', { autoClose: 2000 })
      return
    }
    if (!userId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      return
    }
    if (!canResendSupplierEmail) {
      toast.error('PO must be Approved before resending to supplier', { autoClose: 2000 })
      return
    }
    if (!supplierMailId) {
      toast.error('Supplier email is missing', { autoClose: 2000 })
      return
    }
    if (!isValidEmailAddress(supplierMailId)) {
      toast.error(`Invalid supplier email: ${supplierMailId}`, { autoClose: 2000 })
      return
    }

    const ccEmails = buildCcEmails(userEmail, additionalCcEmails)
    const invalidCc = ccEmails.find((email) => !isValidEmailAddress(email))
    if (invalidCc) {
      toast.error(`Invalid CC email: ${invalidCc}`, { autoClose: 2000 })
      return
    }

    setResendingSupplierFor(supplierMailId)
    try {
      const response = await axios.post(API_URL, {
        action: 'resend-po-supplier-email',
        id,
        userid: userId,
        supplierMailId,
        ccEmails
      })
      const responseData = parseApiResponse(response.data)

      if (isApiSuccess(responseData)) {
        const failCount = Number(responseData?.data?.failCount) || 0
        if (failCount > 0) {
          toast.error(
            responseData?.message || `Failed to resend email to ${supplierMailId}`,
            { autoClose: 2000 }
          )
          return
        }
        toast.success(
          responseData?.message || `PO email resent to ${supplierMailId}`,
          { autoClose: 2000 }
        )
        setPo((prev) => (prev ? { ...prev, isMailSent: true } : prev))
      } else {
        toast.error(
          responseData?.message || `Failed to resend email to ${supplierMailId}`,
          { autoClose: 2000 }
        )
      }
    } catch (error) {
      console.error('Resend PO supplier email error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(
        errData?.message || 'An error occurred while resending supplier email',
        { autoClose: 2000 }
      )
    } finally {
      setResendingSupplierFor('')
    }
  }

  const handleViewPdf = () => {
    const url = po?.pdfDownloadUrl || po?.pdfLink
    if (!url) {
      toast.error('PDF is not available for this PO', { autoClose: 2000 })
      return
    }
    setIsOpeningPdf(true)
    try {
      window.open(url, '_blank', 'noopener,noreferrer')
    } finally {
      setIsOpeningPdf(false)
    }
  }

  const downloadPoOwnerAttachment = async (s3Key) => {
    if (!s3Key || downloadingS3Key) return
    setDownloadingS3Key(s3Key)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-po-owner-attachment-download-presigned-url',
        s3Keys: [s3Key],
        expiresIn: 3600
      })
      const responseData = parseApiResponse(response.data)
      if (!isApiSuccess(responseData)) {
        toast.error(responseData?.message || 'Failed to get download URL', { autoClose: 2000 })
        return
      }
      const downloadUrl =
        responseData.files?.[0]?.downloadUrl ||
        responseData.downloadUrls?.[0] ||
        responseData.downloadUrl
      if (!downloadUrl) {
        toast.error('Download URL not available', { autoClose: 2000 })
        return
      }
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Download PO owner attachment error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(errData?.message || 'Failed to download attachment', { autoClose: 2000 })
    } finally {
      setDownloadingS3Key('')
    }
  }

  const openMilestoneAttachment = async (attachment) => {
    const s3Key = attachment?.s3Key
    if (!s3Key || downloadingS3Key) return

    const directUrl = attachment.viewUrl || attachment.downloadUrl || attachment.url
    if (directUrl) {
      window.open(directUrl, '_blank', 'noopener,noreferrer')
      return
    }

    setDownloadingS3Key(s3Key)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-po-milestone-attachment-download-presigned-url',
        s3Keys: [s3Key]
      })
      const responseData = parseApiResponse(response.data)
      if (!isApiSuccess(responseData)) {
        toast.error(responseData?.message || 'Failed to get download URL', { autoClose: 2000 })
        return
      }
      const openUrl =
        responseData.files?.[0]?.viewUrl ||
        responseData.files?.[0]?.downloadUrl ||
        responseData.viewUrl ||
        responseData.downloadUrl
      if (!openUrl) {
        toast.error('Download URL not available', { autoClose: 2000 })
        return
      }
      window.open(openUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Open milestone attachment error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(errData?.message || 'Failed to open milestone attachment', { autoClose: 2000 })
    } finally {
      setDownloadingS3Key('')
    }
  }

  const header = (
    <div className="content-header">
      <div className="container-fluid">
        <div className="row mb-2 align-items-center">
          <div className="col-6 col-sm-6">
            <h1 className="m-0 txt_lft">View Purchase Order</h1>
          </div>
          <div className="col-6 col-sm-6">
            <ol className="breadcrumb justify-content-end mb-0">
              <li className="breadcrumb-item">
                <Link to="/po">
                  <i className="fa-solid fa-arrow-left-long" /> Back
                </Link>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  )

  if (isLoading) {
    return (
      <div>
        {header}
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!po) {
    return (
      <div>
        {header}
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <p>PO not found</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const poNo = po.poNumber || '-'

  return (
    <div>
      {header}
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <div className="card-body">
                    <SectionTitle icon="fa-file-lines" title="PO Details" />

                    <ViewField label="PO Number">{poNo}</ViewField>
                    <ViewField label="PO Date">{formatDate(po.poDate)}</ViewField>
                    <ViewField label="Delivery Date">{formatDate(po.poDeliveryDate)}</ViewField>
                    <ViewField label="Currency">{po.poCurrency || '-'}</ViewField>
                    <ViewField label="PO Status">
                      <span className="fwt_600">{po.poStatus || '-'}</span>
                    </ViewField>
                    <ViewField label="Status">
                      <span
                        className={`badge ${
                          String(po.status).toLowerCase() === 'active'
                            ? 'badge-success'
                            : 'badge-secondary'
                        }`}
                        style={{ fontSize: '90%', fontWeight: 550 }}
                      >
                        {capitalizeStatus(po.status)}
                      </span>
                    </ViewField>
                    <ViewField label="Approver">{approverName || '-'}</ViewField>
                    <ViewField label="PO Summary">
                      <span style={{ whiteSpace: 'pre-wrap' }}>{po.poSummary || '-'}</span>
                    </ViewField>

                    <SectionTitle icon="fa-building" title="Supplier" />

                    <div className="table-responsive mb-3">
                      <table className="table table-bordered table-sm mb-0">
                        <thead>
                          <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                            <th style={{ fontSize: '12px', minWidth: '180px' }}>Supplier Name</th>
                            <th style={{ fontSize: '12px', minWidth: '180px' }}>Supplier Mail ID</th>
                            <th style={{ fontSize: '12px', minWidth: '140px' }}>Supplier Mobile</th>
                          </tr>
                        </thead>
                        <tbody>
                          {supplierContacts.length === 0 ? (
                            <tr>
                              <td colSpan={3} className="text-center text-muted">
                                No supplier contacts
                              </td>
                            </tr>
                          ) : (
                            supplierContacts.map((contact, index) => (
                              <tr key={`${contact.supplierMailId || 'contact'}-${index}`}>
                                <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                  {contact.supplierName || getSupplierName(po) || '-'}
                                </td>
                                <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                  {contact.supplierMailId || '-'}
                                </td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                  {contact.supplierPhoneNumber || '-'}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    <SectionTitle icon="fa-list-check" title="Terms" />

                    {terms.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">No terms</div>
                    ) : (
                      <div className="table-responsive mb-3">
                        <table className="table table-bordered table-sm mb-0">
                          <thead>
                            <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                              <th style={{ fontSize: '12px', width: '48px' }}>S.No</th>
                              <th style={{ fontSize: '12px', minWidth: '160px' }}>Term</th>
                              <th style={{ fontSize: '12px', minWidth: '200px' }}>Conditions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {terms.map((term, index) => (
                              <tr key={index}>
                                <td>{index + 1}</td>
                                <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                  {term.term || '-'}
                                </td>
                                <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                  {term.response || '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <SectionTitle icon="fa-boxes-stacked" title="Materials" />

                    {materials.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">No materials</div>
                    ) : (
                      <div className="table-responsive mb-3">
                        <table className="table table-bordered table-sm mb-0" style={{ minWidth: '1200px' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                              <th style={{ fontSize: '12px' }}>S.No</th>
                              <th style={{ fontSize: '12px' }}>Material Code</th>
                              <th style={{ fontSize: '12px' }}>Material Description</th>
                              <th style={{ fontSize: '12px' }}>Ref. Number</th>
                              <th style={{ fontSize: '12px' }}>HSN</th>
                              <th style={{ fontSize: '12px' }}>UOM</th>
                              <th style={{ fontSize: '12px' }}>Quantity</th>
                              <th style={{ fontSize: '12px' }}>Basic Price</th>
                              <th style={{ fontSize: '12px' }}>GST %</th>
                              <th style={{ fontSize: '12px' }}>GST Value</th>
                              <th style={{ fontSize: '12px' }}>Total Value</th>
                              <th style={{ fontSize: '12px' }}>Technical Remarks</th>
                            </tr>
                          </thead>
                          <tbody>
                            {materials.map((row, index) => (
                              <tr key={index}>
                                <td>{index + 1}</td>
                                <td>{row.materialCode || '-'}</td>
                                <td
                                  style={{
                                    maxWidth: '240px',
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {row.materialDescription || '-'}
                                </td>
                                <td>{row.referenceNumber || row.prNumber || '-'}</td>
                                <td>{row.hsn || row.hsnCode || '-'}</td>
                                <td>{row.uom || '-'}</td>
                                <td>{row.quantity ?? '-'}</td>
                                <td>{formatMaterialMoney(row.basicPrice)}</td>
                                <td>
                                  {getMaterialGstPercent(row) != null
                                    ? `${getMaterialGstPercent(row)}%`
                                    : '-'}
                                </td>
                                <td>{getMaterialGstValueDisplay(row)}</td>
                                <td>{getMaterialTotalValueDisplay(row)}</td>
                                <td
                                  style={{
                                    maxWidth: '200px',
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {row.technicalRemarks || '-'}
                                </td>
                              </tr>
                            ))}
                            <tr style={{ backgroundColor: '#f4f6f9' }}>
                              <td
                                colSpan={7}
                                className="text-right align-middle"
                                style={{ fontSize: '13px', fontWeight: 600 }}
                              >
                                Gross Total
                                {po.poCurrency ? ` (${po.poCurrency})` : ''}
                              </td>
                              <td className="align-middle" style={{ fontSize: '13px', fontWeight: 600 }}>
                                {formatMaterialMoney(materialGrossTotals.grossTotalBasicPrice)}
                              </td>
                              <td />
                              <td className="align-middle" style={{ fontSize: '13px', fontWeight: 600 }}>
                                {formatMaterialMoney(materialGrossTotals.grossGstValue)}
                              </td>
                              <td className="align-middle" style={{ fontSize: '13px', fontWeight: 600 }}>
                                {formatMaterialMoney(materialGrossTotals.grossTotalValue)}
                              </td>
                              <td />
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}

                    <SectionTitle icon="fa-flag-checkered" title="Milestone" />

                    {milestones.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">No milestones</div>
                    ) : (
                      <div className="table-responsive mb-3">
                        <table className="table table-bordered table-sm mb-0">
                          <thead>
                            <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                              <th style={{ fontSize: '12px' }}>S.No</th>
                              <th style={{ fontSize: '12px' }}>Milestone Activity</th>
                              <th style={{ fontSize: '12px' }}>Action to be Taken</th>
                              <th style={{ fontSize: '12px' }}>Responsibility</th>
                              <th style={{ fontSize: '12px' }}>Attachments</th>
                            </tr>
                          </thead>
                          <tbody>
                            {milestones.map((row, index) => {
                              const milestoneAttachments = Array.isArray(row.attachments)
                                ? row.attachments
                                : []
                              return (
                              <tr key={index}>
                                <td>{index + 1}</td>
                                <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                  {row.milestoneActivity || '-'}
                                </td>
                                <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                  {row.actionToBeTaken || '-'}
                                </td>
                                <td>
                                  {row.responsibilityUser ||
                                    row.responsibilityUserDetails?.name ||
                                    '-'}
                                </td>
                                <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                  {milestoneAttachments.length === 0 ? (
                                    '-'
                                  ) : (
                                    <div>
                                      {milestoneAttachments.map((att, attIndex) => {
                                        const s3Key = att.s3Key || ''
                                        const isOpening = downloadingS3Key === s3Key
                                        return (
                                          <div
                                            key={s3Key || attIndex}
                                            className="d-flex align-items-center justify-content-between py-1"
                                            style={{
                                              borderBottom:
                                                attIndex < milestoneAttachments.length - 1
                                                  ? '1px solid #eef1f4'
                                                  : undefined,
                                              gap: '8px'
                                            }}
                                          >
                                            <div style={{ fontSize: '12px', minWidth: 0 }}>
                                              <div className="text-truncate" title={att.originalFileName || s3Key}>
                                                {att.originalFileName || att.fileName || s3Key || '-'}
                                              </div>
                                              {att.uploadedAt ? (
                                                <small className="text-muted">
                                                  {formatDateTime(att.uploadedAt)}
                                                </small>
                                              ) : null}
                                            </div>
                                            {s3Key ? (
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-outline-primary"
                                                title="Open attachment"
                                                onClick={() => openMilestoneAttachment(att)}
                                                disabled={isOpening || Boolean(downloadingS3Key)}
                                                style={{ flexShrink: 0 }}
                                              >
                                                {isOpening ? (
                                                  <i className="fa-solid fa-spinner fa-spin" />
                                                ) : (
                                                  <i className="fa-solid fa-up-right-from-square" />
                                                )}
                                              </button>
                                            ) : null}
                                          </div>
                                        )
                                      })}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )})}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <SectionTitle icon="fa-paperclip" title="PO Owner Attachments" />

                    {owners.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">No attachments</div>
                    ) : (
                      <div className="table-responsive mb-3">
                        <table className="table table-bordered table-sm mb-0">
                          <thead>
                            <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                              <th style={{ fontSize: '12px' }}>S.No</th>
                              <th style={{ fontSize: '12px' }}>File Name</th>
                              <th style={{ fontSize: '12px' }}>Uploaded At</th>
                              <th style={{ fontSize: '12px', width: '90px' }}>View</th>
                            </tr>
                          </thead>
                          <tbody>
                            {owners.map((att, index) => {
                              const s3Key = att.s3Key || ''
                              const isDownloading = downloadingS3Key === s3Key
                              return (
                                <tr key={s3Key || index}>
                                  <td>{index + 1}</td>
                                  <td>{att.originalFileName || att.fileName || s3Key || '-'}</td>
                                  <td>{formatDateTime(att.uploadedAt)}</td>
                                  <td className="text-center">
                                    {s3Key && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        title="Open attachment"
                                        onClick={() => downloadPoOwnerAttachment(s3Key)}
                                        disabled={isDownloading || Boolean(downloadingS3Key)}
                                      >
                                        {isDownloading ? (
                                          <i className="fa-solid fa-spinner fa-spin" />
                                        ) : (
                                          <i className="fa-solid fa-up-right-from-square" />
                                        )}
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {showResendEmailSection && (
                      <>
                        <SectionTitle icon="fa-envelope" title="Resend PO Email" />

                        <div className="row mb-3">
                          <div className="col-md-6">
                            <div className="form-group mb-2">
                              <label>CC Emails (your email)</label>
                              <div className="txtdsply" style={{ fontSize: '13px' }}>
                                {userEmail || (
                                  <span className="text-muted">
                                    Profile email not available — add CC addresses below
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-2">
                              <label>Additional CC Emails</label>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="e.g. manager@example.com"
                                value={additionalCcEmails}
                                onChange={(e) => setAdditionalCcEmails(e.target.value)}
                                disabled={isResending || Boolean(resendingSupplierFor)}
                              />
                              <small className="form-text text-muted">
                                Comma-separated. Included when resending email.
                              </small>
                              {buildCcEmails(userEmail, additionalCcEmails).length > 0 && (
                                <small className="form-text text-muted d-block">
                                  Will CC: {buildCcEmails(userEmail, additionalCcEmails).join(', ')}
                                </small>
                              )}
                            </div>
                          </div>
                        </div>

                        {canResendSupplierEmail && (
                          <>
                            <h6 className="text-muted mb-2" style={{ fontSize: '13px', fontWeight: 600 }}>
                              Resend to Supplier
                            </h6>
                            <div className="table-responsive mb-3">
                              <table className="table table-bordered table-sm mb-0">
                                <thead>
                                  <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                    <th style={{ fontSize: '12px' }}>S.No</th>
                                    <th style={{ fontSize: '12px' }}>Supplier Name</th>
                                    <th style={{ fontSize: '12px' }}>Supplier Mail ID</th>
                                    <th style={{ fontSize: '12px', width: '100px' }}>Resend</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {supplierContacts.length === 0 ? (
                                    <tr>
                                      <td colSpan={4} className="text-center text-muted">
                                        No supplier contacts
                                      </td>
                                    </tr>
                                  ) : (
                                    supplierContacts.map((contact, index) => {
                                      const supplierMailId = String(
                                        contact.supplierMailId || ''
                                      ).trim()
                                      const isResendingThis =
                                        resendingSupplierFor === supplierMailId
                                      return (
                                        <tr
                                          key={`resend-supplier-${supplierMailId || 'contact'}-${index}`}
                                        >
                                          <td>{index + 1}</td>
                                          <td
                                            style={{
                                              whiteSpace: 'normal',
                                              wordBreak: 'break-word'
                                            }}
                                          >
                                            {contact.supplierName || getSupplierName(po) || '-'}
                                          </td>
                                          <td
                                            style={{
                                              whiteSpace: 'normal',
                                              wordBreak: 'break-word'
                                            }}
                                          >
                                            {supplierMailId || '-'}
                                          </td>
                                          <td className="text-center">
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-outline-primary"
                                              title={
                                                supplierMailId
                                                  ? `Resend PO email to ${supplierMailId}`
                                                  : 'Supplier email missing'
                                              }
                                              onClick={() => handleResendSupplierEmail(contact)}
                                              disabled={
                                                !supplierMailId ||
                                                isResendingThis ||
                                                Boolean(resendingSupplierFor) ||
                                                isResending
                                              }
                                            >
                                              {isResendingThis ? (
                                                <i className="fa-solid fa-spinner fa-spin" />
                                              ) : (
                                                <i className="fa-solid fa-paper-plane" />
                                              )}
                                            </button>
                                          </td>
                                        </tr>
                                      )
                                    })
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}

                        {canResendApproverEmail && (
                          <>
                            <h6 className="text-muted mb-2" style={{ fontSize: '13px', fontWeight: 600 }}>
                              Resend to Approver
                            </h6>
                            <div className="table-responsive mb-3">
                              <table className="table table-bordered table-sm mb-0">
                                <thead>
                                  <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                    <th style={{ fontSize: '12px' }}>S.No</th>
                                    <th style={{ fontSize: '12px' }}>Approver Name</th>
                                    <th style={{ fontSize: '12px' }}>Email</th>
                                    <th style={{ fontSize: '12px', width: '100px' }}>Resend</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <td>1</td>
                                    <td>{approverName || '-'}</td>
                                    <td>{approverEmail || '-'}</td>
                                    <td className="text-center">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        title="Resend PO email to approver"
                                        onClick={handleResendEmail}
                                        disabled={
                                          !approverEmail ||
                                          isResending ||
                                          Boolean(resendingSupplierFor)
                                        }
                                      >
                                        {isResending ? (
                                          <i className="fa-solid fa-spinner fa-spin" />
                                        ) : (
                                          <i className="fa-solid fa-paper-plane" />
                                        )}
                                      </button>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}
                      </>
                    )}

                    {hasPoPdf(po) ? (
                      <>
                        <SectionTitle icon="fa-file-pdf" title="Document" />
                        <ViewField label="PDF Link">
                          <button
                            type="button"
                            className="btn btn-link p-0 align-baseline"
                            onClick={handleViewPdf}
                            disabled={isOpeningPdf}
                          >
                            {isOpeningPdf ? (
                              <>
                                <i className="fa-solid fa-spinner fa-spin mr-1" />
                                Opening...
                              </>
                            ) : (
                              'View PDF'
                            )}
                          </button>
                        </ViewField>
                      </>
                    ) : null}

                    <SectionTitle icon="fa-clock" title="Audit" />

                    <ViewField label="Created By">{displayUser(po.createdBy)}</ViewField>
                    <ViewField label="Updated By">{displayUser(po.updatedBy)}</ViewField>
                    <ViewField label="Created At">{formatDateTime(po.createdAt)}</ViewField>
                    <ViewField label="Updated At">{formatDateTime(po.updatedAt)}</ViewField>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PoView